import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SchemeService } from '../../../services/scheme.service';
import { DepartmentMasterService } from '../../../services/department-master.service';
import { Scheme, Department } from '../../../models/user.model';
import { DataTableComponent, TableColumn } from '../../../components/shared/data-table/data-table.component';
import { AdminModalComponent } from '../../../components/shared/admin-modal/admin-modal.component';

@Component({
  selector: 'app-scheme-management',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    DataTableComponent, 
    AdminModalComponent
  ],
  templateUrl: './scheme-management.component.html',
  styleUrls: ['./scheme-management.component.scss']
})
export class SchemeManagementComponent implements OnInit {
  private schemeService = inject(SchemeService);
  private deptService = inject(DepartmentMasterService);
  private fb = inject(FormBuilder);

  schemes = signal<Scheme[]>([]);
  departments = signal<Department[]>([]);
  loading = signal(false);
  isModalOpen = signal(false);
  isSubmitting = signal(false);

  pendingSchemes = computed(() => this.schemes().filter(s => s.status === 'pending').length);
  approvedSchemes = computed(() => this.schemes().filter(s => s.status === 'approved').length);
  
  schemeForm: FormGroup;
  editingScheme: Scheme | null = null;

  columns: TableColumn[] = [
    { key: 'code', label: 'Code', sortable: true },
    { key: 'name', label: 'Scheme Name', sortable: true },
    { key: 'status', label: 'Status', type: 'badge' },
    { key: 'actions', label: 'Actions', type: 'actions' }
  ];

  constructor() {
    this.schemeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      code: ['', Validators.required],
      deptId: ['', Validators.required],
      description: [''],
      remarks: ['']
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  async loadData() {
    this.loading.set(true);
    try {
      const [allSchemes, allDepts] = await Promise.all([
        this.schemeService.getMySchemes(),
        this.deptService.getDepartments()
      ]);
      this.schemes.set(allSchemes);
      this.departments.set(allDepts.filter(d => d.isActive));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.loading.set(false);
    }
  }

  openCreateModal() {
    this.editingScheme = null;
    this.schemeForm.reset();
    this.isModalOpen.set(true);
  }

  openEditModal(scheme: Scheme) {
    this.editingScheme = scheme;
    this.schemeForm.patchValue(scheme);
    this.isModalOpen.set(true);
  }

  async handleTableAction(event: { type: string, item: any }) {
    if (event.type === 'edit') {
      this.openEditModal(event.item);
    }
  }

  async submitForm() {
    if (this.schemeForm.invalid) return;

    this.isSubmitting.set(true);
    try {
      if (this.editingScheme) {
        await this.schemeService.updateScheme(this.editingScheme.id, this.schemeForm.value);
      } else {
        await this.schemeService.createScheme(this.schemeForm.value);
      }
      this.isModalOpen.set(false);
      this.loadData();
    } catch (error) {
      console.error('Error saving scheme:', error);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
